/**
 * IntegrityPanel — surfaces resume honesty deviations to the user.
 *
 * Loads `/api/integrity/{analysisId}` and renders:
 *   - A compact banner with the integrity score 0-100 + 1-sentence verdict
 *   - Each flag as a COLLAPSIBLE row (severity icon, claim) → expand to see
 *     reason + grounded alternative + action buttons.
 *
 * This mirrors the Role Strategy Brief presentation pattern: clean headers,
 * everything tucked behind a click so the page never overwhelms.
 */
import React, { useEffect, useRef, useState } from "react";
import ProgressMeter from "./ProgressMeter";
import {
  AlertTriangle, AlertCircle, CheckCircle2, ShieldCheck, Loader, RefreshCw,
  Check, Pencil, X, ChevronDown, ChevronUp, ArrowRight, FileWarning,
} from "lucide-react";
import { api } from "../lib/api";
import AuthenticitySeal from "./AuthenticitySeal";

// Delta-kind meta — chip color + label + short user-facing description used
// in the side-by-side diff view. Order of keys mirrors severity: reds first.
const DELTA_META = {
  invented:       { label: "Invented",       chip: "#B91C1C", bg: "#FEE2E2",
                    hint: "No anchor in your base résumé — Resmo generated this from scratch." },
  scope_expanded: { label: "Scope expanded", chip: "#B91C1C", bg: "#FEE2E2",
                    hint: "Team size / budget / impact was stretched beyond what your base says." },
  added:          { label: "Added detail",   chip: "#CA8A04", bg: "#FEF3C7",
                    hint: "A concrete number, tool, or name got added that isn't in your base." },
  amplified:      { label: "Amplified",      chip: "#CA8A04", bg: "#FEF3C7",
                    hint: "A verb or scope reads stronger than your base actually claimed." },
  generalized:    { label: "Generalized",    chip: "#0E5A4A", bg: "#D1FAE5",
                    hint: "A specific base detail was replaced with a vaguer claim — harder to verify." },
};

const SEVERITY_META = {
  red: {
    label:   "Likely fabrication",
    color:   "#B91C1C",
    soft:    "#FEE2E2",
    icon:    AlertTriangle,
  },
  yellow: {
    label:   "Expansion",
    color:   "#CA8A04",
    soft:    "#FEF3C7",
    icon:    AlertCircle,
  },
};

function tone(score) {
  if (score == null) return { color: "#94A3B8", soft: "#F1F5F9", label: "—" };
  if (score >= 90)   return { color: "#0E5A4A", soft: "#CFFAFE", label: "Honest" };
  if (score >= 75)   return { color: "#047857", soft: "#D1FAE5", label: "Mostly honest" };
  if (score >= 55)   return { color: "#CA8A04", soft: "#FEF3C7", label: "Some embellishment" };
  return                       { color: "#B91C1C", soft: "#FEE2E2", label: "Needs review" };
}

export default function IntegrityPanel({ analysisId, compact = false, onApplied }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(null);  // flag_id while mutating
  const [editingId, setEditingId] = useState(null);
  const [editText, setEditText] = useState("");
  const [triggering, setTriggering] = useState(false);
  const [error, setError] = useState("");
  const [studioApplied, setStudioApplied] = useState(false);
  const [expandedIds, setExpandedIds] = useState(new Set());
  const pollRef = useRef(null);
  // Tracks the previous render's score so the 🎉 celebration toast only
  // fires on a *transition* from <100 → ≥100 (never on first paint when
  // the doc is already at 100). Initialised to null so the first effect
  // run cannot mistake "freshly hydrated" for "just crossed the line".
  const prevScoreRef = useRef(null);
  // Remembers which analysisId we've already celebrated for, so HMR /
  // re-mounts don't fire the toast a second time for the same doc.
  const celebratedForRef = useRef(null);
  const [celebrate, setCelebrate] = useState(false);
  // Verify-all destructive-confirm modal state. We deliberately do NOT use
  // window.confirm() because attesting to every claim has integrity stakes,
  // so the user must tick a checkbox before the confirm button enables.
  const [verifyAllOpen, setVerifyAllOpen] = useState(false);
  const [verifyAllChecked, setVerifyAllChecked] = useState(false);
  const currentScore = data?.integrity_score ?? null;
  const hasFlags = (data?.flags || []).length > 0;
  useEffect(() => {
    // No score yet (still loading / errored) — just record and bail.
    if (currentScore == null || !hasFlags) {
      prevScoreRef.current = currentScore;
      return;
    }
    const prev = prevScoreRef.current;
    // Only celebrate when we *observed* a sub-100 score on a prior render
    // and now see ≥100. `prev === null` (first paint) intentionally skips.
    if (
      prev !== null &&
      prev < 100 &&
      currentScore >= 100 &&
      celebratedForRef.current !== analysisId
    ) {
      celebratedForRef.current = analysisId;
      setCelebrate(true);
      const tm = setTimeout(() => setCelebrate(false), 4500);
      prevScoreRef.current = currentScore;
      return () => clearTimeout(tm);
    }
    prevScoreRef.current = currentScore;
  }, [currentScore, hasFlags, analysisId]);

  const toggleExpanded = (id) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const expandAll  = () => setExpandedIds(new Set((data?.flags || []).map((f) => f.id)));
  const collapseAll = () => setExpandedIds(new Set());

  const load = async () => {
    try {
      const { data: d } = await api.get(`/integrity/${analysisId}`);
      setData(d);
      // If still running, poll every 4s.
      if (d.status === "running") {
        clearTimeout(pollRef.current);
        pollRef.current = setTimeout(load, 4000);
      }
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't load integrity check.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (analysisId) load();
    return () => clearTimeout(pollRef.current);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [analysisId]);

  const trigger = async () => {
    setTriggering(true);
    setError("");
    try {
      await api.post("/integrity/check", { analysis_id: analysisId });
      // Optimistically flip to running so the spinner shows.
      setData((d) => ({ ...(d || {}), status: "running" }));
      setTimeout(load, 4000);
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't start integrity check.");
    } finally {
      setTriggering(false);
    }
  };

  const updateFlag = async (flagId, status, replacement) => {
    setSubmitting(flagId);
    try {
      const body = { status };
      if (status === "replaced") body.replacement = replacement || "";
      const resp = await api.post(`/integrity/${analysisId}/flag/${flagId}`, body);
      if (resp.data?.applied_to_studio) {
        setError("");
        // Surface a tiny toast-y banner inline.
        setStudioApplied(true);
        setTimeout(() => setStudioApplied(false), 4000);
      }
      await load();
      if (onApplied) onApplied({ flagId, status, replacement });
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't update flag.");
    } finally {
      setSubmitting(null);
      setEditingId(null);
      setEditText("");
    }
  };

  /**
   * One-click resolve every still-pending flag. Two modes:
   *   - "approve_all"      → mark every pending flag as "I verify this"
   *   - "use_grounded_all" → swap every pending flag for its grounded
   *                          alternative (skips flags that don't have one).
   * Used by the "Verify all" / "Use grounded for all" toolbar buttons so the
   * user can drive the score to 100 in a single click instead of 8.
   */
  const bulkUpdate = async (action) => {
    setSubmitting("__bulk__");
    setError("");
    try {
      const resp = await api.post(`/integrity/${analysisId}/bulk`, { action });
      if ((resp.data?.studio_applied || 0) > 0) {
        setStudioApplied(true);
        setTimeout(() => setStudioApplied(false), 4000);
      }
      await load();
      if (onApplied) onApplied({ bulk: true, action, ...resp.data });
    } catch (e) {
      setError(e?.response?.data?.detail || "Couldn't apply bulk action.");
    } finally {
      setSubmitting(null);
    }
  };

  if (loading) {
    return (
      <div className="rm-card-static p-3 flex items-center gap-2 text-sm"
           style={{ background: "#F8FAFC", borderColor: "#0F1B2D", color: "#475569" }}
           data-testid="integrity-loading">
        <Loader size={14} className="animate-spin" />
        Loading honesty check…
      </div>
    );
  }

  // ABSENT (never run) — show CTA banner
  if (!data || data.status === "absent" || (!data.status && !data.flags)) {
    return (
      <div className="rm-card-static p-3"
           style={{ background: "#FFFBEB", border: "2px dashed #FFC145", color: "#0F1B2D" }}
           data-testid="integrity-absent">
        <div className="flex items-center gap-2 flex-wrap">
          <ShieldCheck size={16} style={{ color: "#FF6A00" }} />
          <span className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#B45309", fontWeight: 700 }}>
            · Resume honesty check
          </span>
          <span className="text-[12.5px] flex-1 min-w-[200px]">
            Resmo can audit the tailored resume against your original to flag anything fabricated.
          </span>
          <button
            type="button"
            onClick={trigger}
            disabled={triggering}
            className="rm-btn rm-btn-primary text-xs px-3 py-1.5"
            data-testid="integrity-run-btn"
          >
            {triggering ? <Loader size={12} className="animate-spin" /> : <ShieldCheck size={12} />}
            {triggering ? "Starting…" : "Run honesty check"}
          </button>
        </div>
      </div>
    );
  }

  // RUNNING
  if (data.status === "running") {
    return (
      <div className="rm-card-static p-3"
           style={{ background: "#FFF7ED", borderColor: "#FF6A00", color: "#0F1B2D" }}
           data-testid="integrity-running">
        <div className="flex items-center gap-2 mb-2">
          <Loader size={14} className="animate-spin rm-text-orange" />
          <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ color: "#B45309", fontWeight: 700 }}>
            · Auditing the tailored resume against your original.
          </div>
        </div>
        <ProgressMeter expectedSeconds={30} testid="integrity-progress-meter" />
      </div>
    );
  }

  // ERROR
  if (data.status === "error") {
    return (
      <div className="rm-card-static p-3"
           style={{ background: "#FEF2F2", borderColor: "#B91C1C", color: "#991B1B" }}
           data-testid="integrity-error">
        <div className="flex items-center gap-2 flex-wrap">
          <AlertCircle size={14} />
          <span className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ fontWeight: 700 }}>
            · Honesty check couldn't complete
          </span>
          <span className="text-[12.5px] flex-1 min-w-[200px]">{data.error || "Resmo will try again shortly."}</span>
          <button
            type="button"
            onClick={trigger}
            disabled={triggering}
            className="rm-btn rm-btn-ghost text-xs px-3 py-1.5"
            data-testid="integrity-retry-btn"
          >
            <RefreshCw size={12} /> Retry
          </button>
        </div>
      </div>
    );
  }

  // COMPLETE
  const flags = data.flags || [];
  const score = data.integrity_score ?? 100;
  const t = tone(score);
  const reds = flags.filter((f) => f.severity === "red");
  const yellows = flags.filter((f) => f.severity === "yellow");
  const pending = flags.filter((f) => f.status === "pending");
  const pendingWithGrounded = pending.filter((f) => (f.grounded_alternative || "").trim().length > 0);
  // Verified = an actual integrity check ran (flags > 0), score is 100, and
  // there's nothing still pending. Drives whether the gold Authenticity Seal
  // appears in the banner. (When the LLM happened to find zero issues we
  // also hit 100, but a seal there feels unearned — the user never had to
  // act on anything — so we gate on flags > 0.)
  const verified = flags.length > 0 && score >= 100 && pending.length === 0;

  // Compact mode: just the banner, no list
  if (compact) {
    return (
      <div
        className="rm-card-static p-3 flex items-center gap-2 flex-wrap"
        style={{ background: t.soft, borderColor: t.color, color: t.color }}
        data-testid="integrity-banner-compact"
      >
        <ShieldCheck size={16} />
        <span className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ fontWeight: 700 }}>
          · Honesty score · {score}/100 · {t.label}
        </span>
        {pending.length > 0 && (
          <span className="text-[12px] font-semibold">
            {pending.length} claim{pending.length === 1 ? "" : "s"} need review
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-3" data-testid="integrity-panel">
      {/* Banner — compact summary, no big circle */}
      <div
        className="rm-card-static p-3 md:p-4 flex items-center gap-3 flex-wrap"
        style={{ background: t.soft, borderColor: t.color, color: t.color }}
        data-testid="integrity-banner"
      >
        {verified ? (
          <AuthenticitySeal verified size={72} testId="integrity-seal-banner" />
        ) : null}
        <div className="flex items-center gap-2 flex-shrink-0">
          {!verified && <ShieldCheck size={18} />}
          <div>
            <div className="font-mono-rm uppercase text-[10px] tracking-widest" style={{ fontWeight: 700 }}>
              · Resume honesty check
            </div>
            <div className="font-display uppercase leading-tight"
                 style={{ fontSize: "1.05rem", color: "#0F1B2D", letterSpacing: "-0.02em" }}
                 data-testid="integrity-score">
              {score}/100 · {t.label}
            </div>
          </div>
        </div>
        <div className="flex-1 min-w-[180px] text-[12.5px] leading-snug" style={{ color: "#0F1B2D" }}>
          {data.summary}
        </div>
        <button
          type="button"
          onClick={trigger}
          disabled={triggering}
          className="rm-btn rm-btn-ghost text-[10px] px-2 py-1 flex-shrink-0"
          data-testid="integrity-rerun-btn"
          title="Re-run after editing your resume"
        >
          <RefreshCw size={10} /> Re-run check
        </button>
      </div>

      {error && (
        <div className="rm-card-static p-2 text-xs font-mono-rm"
             style={{ background: "#FEE2E2", borderColor: "#B91C1C", color: "#991B1B" }}>
          · {error}
        </div>
      )}

      {studioApplied && (
        <div className="rm-card-static p-2.5 text-xs font-mono-rm uppercase tracking-widest"
             style={{ background: "#ECFDF5", borderColor: "#047857", color: "#047857", fontWeight: 700 }}
             data-testid="integrity-studio-applied">
          · Done — your Studio draft now uses the honest version. Re-download to grab it.
        </div>
      )}

      {celebrate && (
        <div
          className="rm-card-static p-3 flex items-center gap-2.5 flex-wrap"
          style={{
            background: "linear-gradient(135deg, #FFFBEB 0%, #ECFDF5 100%)",
            borderColor: "#047857",
            boxShadow: "4px 4px 0 0 #FFC145",
          }}
          data-testid="integrity-celebrate"
          role="status"
          aria-live="polite"
        >
          <span style={{ fontSize: 22 }} aria-hidden="true">🎉</span>
          <div className="flex-1 min-w-0">
            <div className="font-display uppercase leading-tight"
                 style={{ fontSize: "0.95rem", color: "#0F1B2D", letterSpacing: "-0.02em" }}>
              100/100 · Every claim verified
            </div>
            <div className="text-[12px] mt-0.5" style={{ color: "#0E5A4A" }}>
              Resume Monster certified. You're cleared to download — and recruiters can trust every line.
            </div>
          </div>
        </div>
      )}

      {/* Flags */}
      {flags.length > 0 && (
        <>
          {/* Expand/collapse + bulk-resolve toolbar */}
          <div className="flex items-center justify-between flex-wrap gap-2"
               data-testid="integrity-flag-toolbar">
            <div className="font-mono-rm uppercase text-[10px] tracking-widest"
                 style={{ color: "#475569", fontWeight: 700 }}>
              · {flags.length} flag{flags.length === 1 ? "" : "s"}
              {pending.length > 0 && (
                <span style={{ color: "#B91C1C" }}> · {pending.length} pending</span>
              )}
            </div>
            <div className="flex gap-2 flex-wrap">
              {pending.length > 0 && (
                <>
                  {pendingWithGrounded.length > 0 && (
                    <button
                      type="button"
                      onClick={() => bulkUpdate("use_grounded_all")}
                      disabled={submitting === "__bulk__"}
                      className="rm-btn rm-btn-primary text-[10px] px-2.5 py-1"
                      style={{ background: "#0E5A4A", borderColor: "#0E5A4A", color: "white", fontWeight: 700 }}
                      data-testid="integrity-bulk-use-grounded"
                      title={`Replace all ${pendingWithGrounded.length} remaining flag${pendingWithGrounded.length === 1 ? "" : "s"} with their grounded alternatives.`}
                    >
                      {submitting === "__bulk__" ? <Loader size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
                      Use grounded for all ({pendingWithGrounded.length})
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => {
                      setVerifyAllChecked(false);
                      setVerifyAllOpen(true);
                    }}
                    disabled={submitting === "__bulk__"}
                    className="rm-btn rm-btn-ghost text-[10px] px-2.5 py-1"
                    style={{ borderColor: "#B91C1C", color: "#B91C1C", fontWeight: 700 }}
                    data-testid="integrity-bulk-approve"
                    title={`Mark every pending flag as personally verified. You're attesting that all ${pending.length} claims are accurate.`}
                  >
                    {submitting === "__bulk__" ? <Loader size={11} className="animate-spin" /> : <Check size={11} />}
                    Verify all ({pending.length})
                  </button>
                </>
              )}
              <button type="button" onClick={expandAll}
                      className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                      data-testid="integrity-expand-all">
                <ChevronDown size={11} /> Expand all
              </button>
              <button type="button" onClick={collapseAll}
                      className="rm-btn rm-btn-ghost text-[10px] px-2 py-1"
                      data-testid="integrity-collapse-all">
                <ChevronUp size={11} /> Collapse all
              </button>
            </div>
          </div>

          <div className="space-y-2" data-testid="integrity-flags">
            {flags.map((f) => {
              const meta = SEVERITY_META[f.severity] || SEVERITY_META.yellow;
              const Icon = meta.icon;
              const isResolved = f.status === "approved" || f.status === "replaced";
              const isEditing = editingId === f.id;
              const isOpen = expandedIds.has(f.id) || isEditing;
              return (
                <div
                  key={f.id}
                  className="rm-card-static overflow-hidden"
                  style={{
                    background: isResolved ? "#F8FAFC" : "white",
                    borderColor: isResolved ? "#94A3B8" : meta.color,
                    opacity: isResolved ? 0.78 : 1,
                  }}
                  data-testid={`integrity-flag-${f.id}`}
                >
                  {/* Compact header — click to toggle */}
                  <button
                    type="button"
                    onClick={() => toggleExpanded(f.id)}
                    className="w-full flex items-start gap-2.5 px-3 py-2.5 text-left"
                    style={{ background: isOpen ? meta.soft : "transparent" }}
                    data-testid={`integrity-flag-toggle-${f.id}`}
                  >
                    <div className="flex-shrink-0 mt-0.5">
                      {isResolved
                        ? <CheckCircle2 size={16} style={{ color: "#047857" }} />
                        : <Icon size={16} style={{ color: meta.color }} />}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span
                          className="font-mono-rm uppercase text-[9px] tracking-widest px-1.5 py-0.5"
                          style={{
                            background: meta.color, color: "white",
                            border: `2px solid ${meta.color}`, borderRadius: 4,
                            fontWeight: 700,
                          }}>
                          {meta.label}
                        </span>
                        <span className="font-mono-rm uppercase text-[9px] tracking-widest"
                              style={{ color: "#94A3B8", fontWeight: 700 }}>
                          · {f.section}
                        </span>
                        {isResolved && (
                          <span className="font-mono-rm uppercase text-[9px] tracking-widest"
                                style={{ color: "#047857", fontWeight: 700 }}>
                            · {f.status === "approved" ? "Kept" : "Replaced"}
                          </span>
                        )}
                      </div>
                      <div className="text-[13.5px] mt-1 leading-snug" style={{ color: "#0F1B2D" }}>
                        "{f.claim}"
                      </div>
                    </div>
                    <div className="flex-shrink-0 mt-1">
                      {isOpen
                        ? <ChevronUp size={14} style={{ color: meta.color }} />
                        : <ChevronDown size={14} style={{ color: "#94A3B8" }} />}
                    </div>
                  </button>

                  {/* Expanded body */}
                  {isOpen && (
                    <div className="px-3 pb-3 pt-1"
                         style={{ borderTop: "1px dashed #CBD5E1" }}>
                      <p className="text-[12.5px] leading-snug mt-2" style={{ color: "#475569" }}>
                        <strong style={{ color: meta.color }}>Why flagged:</strong> {f.reason}
                      </p>

                      {/* Side-by-side diff — your base résumé vs. what Resmo
                          wrote, with a delta chip in the middle. Only renders
                          when the LLM captured a base evidence quote OR
                          explicitly flagged NO_MATCH (rendered as an
                          "invented" left column). */}
                      {(() => {
                        const deltaMeta = DELTA_META[f.delta_kind];
                        const hasEvidence = !!f.base_evidence;
                        // We show the diff view whenever we have either a
                        // base evidence quote or a categorised delta_kind.
                        if (!hasEvidence && !deltaMeta) return null;
                        return (
                          <div
                            className="mt-3 rounded overflow-hidden"
                            style={{ border: "1.5px solid #CBD5E1" }}
                            data-testid={`integrity-diff-${f.id}`}
                          >
                            <div className="grid grid-cols-1 md:grid-cols-2">
                              {/* LEFT — base résumé evidence */}
                              <div
                                className="p-2.5"
                                style={{
                                  background: hasEvidence ? "#F8FAFC" : "#FEF2F2",
                                  borderRight: "1px dashed #CBD5E1",
                                }}
                                data-testid={`integrity-diff-base-${f.id}`}
                              >
                                <div className="font-mono-rm uppercase text-[9px] tracking-widest mb-1"
                                     style={{ color: hasEvidence ? "#475569" : "#B91C1C", fontWeight: 700 }}>
                                  · From your base résumé
                                </div>
                                {hasEvidence ? (
                                  <p className="text-[12.5px] leading-snug italic" style={{ color: "#334155" }}>
                                    &ldquo;{f.base_evidence}&rdquo;
                                  </p>
                                ) : (
                                  <div className="flex items-start gap-1.5">
                                    <FileWarning size={14} style={{ color: "#B91C1C", marginTop: 1, flexShrink: 0 }} />
                                    <p className="text-[12.5px] leading-snug" style={{ color: "#7F1D1D", fontWeight: 600 }}>
                                      No anchor found — this is a wholesale new claim.
                                    </p>
                                  </div>
                                )}
                              </div>
                              {/* RIGHT — what Resmo said */}
                              <div
                                className="p-2.5"
                                style={{ background: meta.soft }}
                                data-testid={`integrity-diff-tailored-${f.id}`}
                              >
                                <div className="font-mono-rm uppercase text-[9px] tracking-widest mb-1"
                                     style={{ color: meta.color, fontWeight: 700 }}>
                                  · What Resmo wrote
                                </div>
                                <p className="text-[12.5px] leading-snug" style={{ color: "#0F1B2D", fontWeight: 500 }}>
                                  &ldquo;{f.claim}&rdquo;
                                </p>
                              </div>
                            </div>
                            {/* Delta chip strip */}
                            {deltaMeta && (
                              <div
                                className="flex items-center gap-2 px-2.5 py-1.5 flex-wrap"
                                style={{ background: "white", borderTop: "1px dashed #CBD5E1" }}
                              >
                                <ArrowRight size={12} style={{ color: "#94A3B8", flexShrink: 0 }} aria-hidden />
                                <span
                                  className="font-mono-rm uppercase text-[9px] tracking-widest px-1.5 py-0.5 rounded flex-shrink-0"
                                  style={{
                                    background: deltaMeta.bg,
                                    color: deltaMeta.chip,
                                    fontWeight: 700,
                                    border: `1px solid ${deltaMeta.chip}`,
                                  }}
                                  data-testid={`integrity-diff-chip-${f.id}`}
                                  title={deltaMeta.hint}
                                >
                                  {deltaMeta.label}
                                </span>
                                {f.delta_detail && (
                                  <span className="text-[11.5px] font-medium" style={{ color: "#0F1B2D" }}>
                                    {f.delta_detail}
                                  </span>
                                )}
                                <span className="text-[11px] leading-snug ml-auto italic hidden md:inline"
                                      style={{ color: "#64748B" }}>
                                  {deltaMeta.hint}
                                </span>
                              </div>
                            )}
                          </div>
                        );
                      })()}

                      {f.grounded_alternative && (
                        <div className="mt-2 p-2 rounded" style={{ background: "#F0FDF4", border: "2px dashed #0E5A4A" }}>
                          <div className="font-mono-rm uppercase text-[9px] tracking-widest mb-0.5"
                               style={{ color: "#0E5A4A", fontWeight: 700 }}>
                            · Grounded alternative
                          </div>
                          <p className="text-[13px]" style={{ color: "#0F1B2D" }}>{f.grounded_alternative}</p>
                        </div>
                      )}
                      {f.status === "replaced" && f.replacement && f.replacement !== f.grounded_alternative && (
                        <div className="mt-2 p-2 rounded" style={{ background: "white", border: "2px solid #047857" }}>
                          <div className="font-mono-rm uppercase text-[9px] tracking-widest mb-0.5"
                               style={{ color: "#047857", fontWeight: 700 }}>
                            · Your replacement
                          </div>
                          <p className="text-[13px]" style={{ color: "#0F1B2D" }}>{f.replacement}</p>
                        </div>
                      )}

                      {/* Actions */}
                      {!isResolved && !isEditing && (
                        <div className="flex gap-2 flex-wrap mt-2.5">
                          <button
                            type="button"
                            onClick={() => updateFlag(f.id, "approved")}
                            disabled={submitting === f.id}
                            className="rm-btn rm-btn-ghost text-[11px] px-2.5 py-1.5"
                            data-testid={`integrity-approve-${f.id}`}
                          >
                            <Check size={11} /> I can verify this — keep it
                          </button>
                          {f.grounded_alternative && (
                            <button
                              type="button"
                              onClick={() => updateFlag(f.id, "replaced", f.grounded_alternative)}
                              disabled={submitting === f.id}
                              className="rm-btn rm-btn-primary text-[11px] px-2.5 py-1.5"
                              data-testid={`integrity-use-grounded-${f.id}`}
                            >
                              {submitting === f.id ? <Loader size={11} className="animate-spin" /> : <ShieldCheck size={11} />}
                              Use grounded version
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => {
                              setEditingId(f.id);
                              setEditText(f.grounded_alternative || f.claim);
                            }}
                            className="rm-btn rm-btn-ghost text-[11px] px-2.5 py-1.5"
                            data-testid={`integrity-edit-${f.id}`}
                          >
                            <Pencil size={11} /> Custom rewrite
                          </button>
                        </div>
                      )}
                      {isEditing && (
                        <div className="mt-3">
                          <textarea
                            className="rm-input w-full text-[13px]"
                            rows={3}
                            value={editText}
                            onChange={(e) => setEditText(e.target.value)}
                            data-testid={`integrity-edit-text-${f.id}`}
                          />
                          <div className="flex gap-2 mt-2">
                            <button
                              type="button"
                              onClick={() => updateFlag(f.id, "replaced", editText)}
                              disabled={submitting === f.id || editText.trim().length < 3}
                              className="rm-btn rm-btn-primary text-[11px] px-2.5 py-1.5"
                              data-testid={`integrity-save-${f.id}`}
                            >
                              {submitting === f.id ? <Loader size={11} className="animate-spin" /> : <Check size={11} />}
                              Save my rewrite
                            </button>
                            <button
                              type="button"
                              onClick={() => { setEditingId(null); setEditText(""); }}
                              className="rm-btn rm-btn-ghost text-[11px] px-2.5 py-1.5"
                            >
                              <X size={11} /> Cancel
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Verify-all destructive-confirm modal. Yellow/red palette + a
          mandatory checkbox so the user has to *pause* before attesting
          to every flagged claim. window.confirm() was too easy to fly past. */}
      {verifyAllOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center px-4"
          style={{ background: "rgba(15,27,45,0.6)", backdropFilter: "blur(2px)" }}
          data-testid="integrity-verify-all-modal-backdrop"
          onClick={(e) => {
            if (e.target === e.currentTarget) {
              setVerifyAllOpen(false);
            }
          }}
        >
          <div
            className="rm-card-static max-w-md w-full overflow-hidden"
            style={{
              background: "white",
              borderColor: "#B91C1C",
              borderWidth: 3,
              boxShadow: "8px 8px 0 0 #FACC15, 0 20px 60px rgba(15,27,45,0.4)",
            }}
            role="dialog"
            aria-modal="true"
            aria-labelledby="verify-all-title"
            data-testid="integrity-verify-all-modal"
          >
            {/* Striped warning header */}
            <div
              className="px-4 py-3 flex items-center gap-2"
              style={{
                background:
                  "repeating-linear-gradient(45deg, #FACC15 0 12px, #0F1B2D 12px 14px)",
                color: "#0F1B2D",
              }}
            >
              <div
                className="flex items-center gap-2 px-2 py-1 rounded"
                style={{ background: "#FACC15", border: "2px solid #0F1B2D" }}
              >
                <AlertTriangle size={16} style={{ color: "#B91C1C" }} />
                <span
                  className="font-display uppercase tracking-widest"
                  id="verify-all-title"
                  style={{ fontSize: "0.85rem", color: "#0F1B2D", fontWeight: 800 }}
                >
                  Hold up — this is an attestation
                </span>
              </div>
            </div>

            <div className="p-4">
              <p className="text-sm leading-relaxed mb-3" style={{ color: "#0F1B2D" }}>
                You're about to mark <b style={{ color: "#B91C1C" }}>all {pending.length} remaining
                flag{pending.length === 1 ? "" : "s"}</b> as personally verified. Resmo will treat every one
                of those claims as true on your behalf — including any specific metrics, employers, titles,
                certifications, or scope details flagged by the integrity check.
              </p>
              <div
                className="rm-card-static p-2.5 text-[12px] leading-snug mb-3"
                style={{ background: "#FEF3C7", borderColor: "#D97706", color: "#78350F" }}
              >
                <b>If even one claim is exaggerated</b>, recruiters can catch it in a reference check
                or interview — and your honesty score won't save you. Prefer <b>"Use grounded for all"</b>{" "}
                if you'd rather rewrite them in your own voice.
              </div>

              <label
                className="flex items-start gap-2 cursor-pointer p-2.5 rounded select-none"
                style={{
                  background: verifyAllChecked ? "#FEF3C7" : "#FFFEF7",
                  border: `2px dashed ${verifyAllChecked ? "#D97706" : "#94A3B8"}`,
                  transition: "background-color 0.15s, border-color 0.15s",
                }}
                data-testid="integrity-verify-all-checkbox-label"
              >
                <input
                  type="checkbox"
                  checked={verifyAllChecked}
                  onChange={(e) => setVerifyAllChecked(e.target.checked)}
                  className="mt-1 flex-shrink-0"
                  style={{ width: 18, height: 18, accentColor: "#B91C1C" }}
                  data-testid="integrity-verify-all-checkbox"
                />
                <span className="text-[13px] leading-snug" style={{ color: "#0F1B2D" }}>
                  <b>I personally vouch for every flagged claim.</b> I can defend each one with evidence
                  in an interview or reference check.
                </span>
              </label>

              <div className="flex gap-2 mt-4 justify-end">
                <button
                  type="button"
                  onClick={() => setVerifyAllOpen(false)}
                  className="rm-btn rm-btn-ghost text-[12px] px-3 py-2"
                  data-testid="integrity-verify-all-cancel"
                >
                  <X size={12} /> Cancel
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setVerifyAllOpen(false);
                    setVerifyAllChecked(false);
                    bulkUpdate("approve_all");
                  }}
                  disabled={!verifyAllChecked || submitting === "__bulk__"}
                  className="rm-btn rm-btn-primary text-[12px] px-3 py-2"
                  style={{
                    background: verifyAllChecked ? "#B91C1C" : "#E5E7EB",
                    borderColor: verifyAllChecked ? "#B91C1C" : "#94A3B8",
                    color: verifyAllChecked ? "white" : "#94A3B8",
                    cursor: verifyAllChecked ? "pointer" : "not-allowed",
                    fontWeight: 700,
                  }}
                  data-testid="integrity-verify-all-confirm"
                >
                  <Check size={12} /> Yes, verify all {pending.length}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
